/**
 * Platform-realm session state.
 *
 * Three things this store is responsible for, all of them from CLAUDE.md §6:
 *
 *  1. Login. The platform realm takes email + password, plus a TOTP code once
 *     the account has enrolled.
 *  2. MFA enrolment status. MFA is *mandatory* here: an admin who has not
 *     enrolled can reach `/auth/me` and the enrol/confirm pair and nothing
 *     else — every other route answers `403 ERR_MFA_REQUIRED`. The router
 *     reads `needsMfaEnrolment` to confine them to the enrolment screen, so
 *     that server rule shows up as a guided step rather than a wall of 403s.
 *  3. Refresh. The rotation call itself lives here; the single-flight guard
 *     that makes ten concurrent 401s produce one refresh lives in
 *     `api/client.ts`, which is where the requests are.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as api from '@/api/admin'
import type { User } from '@/api/admin'
import {
  session,
  setRefresher,
  setSession,
  type StoredSession,
} from '@/api/session'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  /** False until the first `/auth/me` has settled, so guards do not act early. */
  const ready = ref(false)

  const isAuthenticated = computed(() => session.value !== null)
  const needsMfaEnrolment = computed(
    () => isAuthenticated.value && user.value !== null && !user.value.mfaEnabled,
  )
  const role = computed(() => user.value?.role ?? null)
  const isOwner = computed(() => role.value === 'owner')
  /** Support helpers are closed to the billing role (§10, routes.go). */
  const canSupport = computed(() => role.value === 'owner' || role.value === 'support')
  const canBilling = computed(() => role.value === 'owner' || role.value === 'billing')

  function store(tokens: StoredSession, profile: User): void {
    setSession(tokens)
    user.value = profile
    ready.value = true
  }

  async function login(email: string, password: string, totpCode?: string): Promise<void> {
    const result = await api.login({
      email,
      password,
      ...(totpCode === undefined || totpCode === '' ? {} : { totpCode }),
    })
    store(
      {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
      },
      result.user,
    )
  }

  /** Re-reads the profile; the source of truth for MFA and role changes. */
  async function loadProfile(): Promise<void> {
    if (session.value === null) {
      user.value = null
      ready.value = true
      return
    }
    try {
      user.value = await api.me()
    } catch {
      // A failed profile read after a failed refresh means the session is gone.
      if (session.value === null) user.value = null
    } finally {
      ready.value = true
    }
  }

  /**
   * Exchanges the refresh token. Returns the new access token, or null when
   * the session cannot be recovered — the caller then sends the operator to
   * the login screen rather than retrying forever.
   */
  async function refreshSession(): Promise<string | null> {
    const current = session.value
    if (current === null) return null
    try {
      const result = await api.refresh(current.refreshToken)
      store(
        {
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresAt: result.expiresAt,
        },
        result.user,
      )
      return result.accessToken
    } catch {
      clear()
      return null
    }
  }

  function clear(): void {
    setSession(null)
    user.value = null
  }

  async function logout(): Promise<void> {
    try {
      await api.logout()
    } catch {
      // A revoked or expired token makes logout a no-op server-side; the local
      // session still has to go, which is what actually matters here.
    } finally {
      clear()
    }
  }

  /** Called after a successful MFA confirmation so guards let the app through. */
  function markMfaEnrolled(): void {
    if (user.value !== null) user.value = { ...user.value, mfaEnabled: true }
  }

  setRefresher(refreshSession)

  return {
    user,
    ready,
    isAuthenticated,
    needsMfaEnrolment,
    role,
    isOwner,
    canSupport,
    canBilling,
    login,
    loadProfile,
    refreshSession,
    logout,
    clear,
    markMfaEnrolled,
  }
})
